import React, { ReactNode } from 'react';
import { Label } from 'src/components/ui';
import classnames from 'src/util/classnames';
import { FieldWidth } from './types';
import { ControlSkeleton, useFormLoading } from './FormLoading';

// Full width on phones. From `sm` up the token is a minimum and the field grows with its content,
// never past its row.
const WIDTH_CLASS: Record<FieldWidth, string> = {
  xs: 'w-full sm:w-auto sm:min-w-24',
  sm: 'w-full sm:w-auto sm:min-w-40',
  md: 'w-full sm:w-auto sm:min-w-56',
  lg: 'w-full sm:w-auto sm:min-w-[22rem]',
  full: 'w-full',
};

export type FieldProps = {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  help?: ReactNode;
  error?: string;
  width?: FieldWidth;
  children: ReactNode;
};

/** Id of the element Field renders for the error or help text, for aria-describedby on the control. */
export function describedById(id: string, error?: string, help?: ReactNode): string | undefined {
  if (error) return `${id}-error`;
  return help ? `${id}-help` : undefined;
}

export function Field({ label, htmlFor, required, help, error: fieldError, width = 'md', children }: FieldProps) {
  // While the record loads the label and help stay, the control becomes a placeholder and no error shows.
  const loading = useFormLoading();
  const error = loading ? undefined : fieldError;

  return (
    <div
      className={classnames('flex flex-col min-w-0 max-w-full', WIDTH_CLASS[width])}
      data-field-width={width}
      data-field-error={error ? 'true' : undefined}
    >
      <Label for={htmlFor} className="text-xs font-medium mb-1">
        {label}
        {required ? (
          <span className="text-red-600 ml-0.5" aria-hidden="true">
            *
          </span>
        ) : null}
      </Label>
      {loading ? <ControlSkeleton /> : children}
      {error ? (
        <div id={htmlFor ? `${htmlFor}-error` : undefined} className="text-xs text-red-600 mt-1" role="alert">
          {error}
        </div>
      ) : help ? (
        <div id={htmlFor ? `${htmlFor}-help` : undefined} className="text-xs opacity-70 mt-1">
          {help}
        </div>
      ) : null}
    </div>
  );
}

/** Lays fields out left to right, wrapping when the card runs out of room. */
export function FieldRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-x-4 gap-y-3">{children}</div>;
}

export default Field;
